import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {chunk} from 'lodash';

import Page from './Page';
import renderTemplate from '../utils/renderTemplate';
import Translation from './Translation';

function splitPages(steps) {
	const ret = [[]];
	steps.forEach(step => {
		if (step.pageBreakBefore) {
			ret.push([step]);
		} else {
			ret[ret.length - 1].push(step);
		}
	});
	return ret;
}

function renderHead({title, exampleTitle, image, exampleText}) {
	return (
		<Fragment>
			<h1>{title}</h1>

			<div className="slx-mounting-mounting__example">
				<div className="slx-mounting-mounting__example-image-wrapper">
					{image && (
						<img
							className="slx-mounting-mounting__example-image"
							src={image}
							/>
					)}
				</div>
				<div className="slx-mounting-mounting__example-content">
					<h2 className="slx-mounting-mounting__example-title">{exampleTitle}</h2>

					<div
						className="slx-mounting-mounting__example-text"
						dangerouslySetInnerHTML={{__html: exampleText}}
						/>

					<div className="slx-mounting-mounting__example-note">
						<Translation name="the-mounting-sequence-is-always-the-same-for-all-luminaire-heads" />
					</div>
				</div>

			</div>
		</Fragment>
	);
}

function intIntoAlpha(i) {
	if (i >= 1 && i <= 26) {
		return String.fromCharCode(96 + i);
	}
	return '?';
}

function renderSubstep({action, tool}, index) {
	return (
		<li className="slx-mounting-mounting__substep" key={index}>
			<span className="slx-mounting-mounting__substep-number">{intIntoAlpha(index + 1)}</span>
			<span className="slx-mounting-mounting__substep-text">
				{action && (
					<span className="slx-mounting-mounting__substep-action">
						{renderTemplate(action.tpl, action.params)}
					</span>
				)}
				{tool && (
					<span className="slx-mounting-mounting__substep-tool">
						{tool.icon && (
							<img className="slx-mounting-mounting__substep-tool-icon" src={tool.icon} />
						)}
						{renderTemplate(tool.tpl, tool.params)}
					</span>
				)}
			</span>
		</li>
	);
}

function renderStep({image, fullImage, note, substeps}, index) {
	let stepContent;
	if (fullImage) {
		stepContent = (
			<img
				className="slx-mounting-mounting__step-full-image"
				src={fullImage}
				/>
		);
	} else {
		stepContent = (
			<Fragment>
				{note &&
					<div className="slx-mounting-mounting__step-note">
						{note}
					</div>
				}
				{image &&
					<div className="slx-mounting-mounting__step-left">
						<img
							className="slx-mounting-mounting__step-image"
							src={image}
							/>
					</div>
				}
				<ol className={`slx-mounting-mounting__substeps${!image ? ' slx-mounting-mounting__substeps--full' : ''}`}>
					{substeps.map(renderSubstep)}
				</ol>
			</Fragment>
		);
	}

	return (
		<li className="slx-mounting-mounting__step" key={index}>
			<span className="slx-mounting-mounting__step-number">{index + 1}</span>
			<div className="slx-mounting-mounting__step-content">
				{stepContent}
			</div>
		</li>
	);
}

function Mounting({content, mounting, startPageNumber, maxPageNumber}) {
	let stepIndex = 0;
	return splitPages(mounting.steps).map((steps, pageIndex) => (
		<Page
			content={content}
			number={startPageNumber + pageIndex}
			maxPageNumber={maxPageNumber}
			key={pageIndex}
		>
			{pageIndex === 0 && renderHead(mounting)}
			<ol className="slx-mounting-mounting__steps">
				{steps.map(step => renderStep(step, stepIndex++))}
			</ol>
		</Page>
	));
}

Mounting.propTypes = {
	content: PropTypes.object.isRequired,
	mounting: PropTypes.object.isRequired,
	startPageNumber: PropTypes.number.isRequired,
	maxPageNumber: PropTypes.number.isRequired,
};

export default React.memo(Mounting);

export function numPages({content: {mounting = []}}) {
	return mounting.map(({steps}) =>
		steps.reduce((acc, {pageBreakBefore: pBB}) => pBB ? acc + 1 : acc, 1)
	);
}
